/**
 * AJ Lead Capture — full-page modal surface.
 *
 * Centered dialog over a scrim. Focus trap, ESC + click-outside + close
 * button dismissal, body scroll lock with scroll-position preservation, and
 * focus restoration to the previously focused element on close.
 *
 * @module modal
 */

import { buildCard } from './markup';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Open a modal for an offer.
 *
 * @param {Object}   offer            Offer config.
 * @param {Object}   [opts]           Options.
 * @param {Function} [opts.onSubmit]  Submit handler(email) -> Promise.
 * @param {Function} [opts.onClose]   Called after the modal closes.
 * @returns {{close: Function, root: HTMLElement}}
 */
export function openModal(offer, opts = {}) {
  const previouslyFocused = document.activeElement;
  const { scrollY } = window;

  const overlay = document.createElement('div');
  overlay.className = 'aj-lo-overlay';

  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');

  let closed = false;

  /**
   * Keyboard handling: ESC closes, Tab is trapped within the dialog.
   *
   * @param {KeyboardEvent} e Event.
   * @returns {void}
   */
  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') {
      return;
    }
    const nodes = dialog.querySelectorAll(FOCUSABLE);
    if (!nodes.length) {
      return;
    }
    const [first] = nodes;
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /**
   * Close the modal, unlock scroll, restore focus.
   *
   * @returns {void}
   */
  function close() {
    if (closed) {
      return;
    }
    closed = true;
    document.removeEventListener('keydown', onKeydown, true);
    overlay.remove();
    document.body.classList.remove('aj-modal-open');

    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    window.scrollTo(0, scrollY);

    if (previouslyFocused && previouslyFocused.focus) {
      previouslyFocused.focus();
    }
    if (opts.onClose) {
      opts.onClose();
    }
  }

  const card = buildCard(offer, {
    showClose: true,
    onClose: close,
    onSubmit: opts.onSubmit,
  });

  if (card.labelId) {
    dialog.setAttribute('aria-labelledby', card.labelId);
  }

  dialog.appendChild(card.root);
  overlay.appendChild(dialog);

  // Click outside (on the scrim) closes.
  overlay.addEventListener('mousedown', (e) => {
    if (e.target === overlay) {
      close();
    }
  });

  document.addEventListener('keydown', onKeydown, true);

  // Lock body scroll (preserve position).
  document.body.style.top = `-${scrollY}px`;
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.overflow = 'hidden';

  // Shared aj-* convention: other plugins' floating widgets (aj-toaster)
  // step aside while any modal is open.
  document.body.classList.add('aj-modal-open');
  document.body.appendChild(overlay);

  if (card.focusTarget) {
    card.focusTarget.focus();
  }

  return { close, root: overlay };
}
